import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { IBackendCartEntries, ILegacyCartEntry, MenuObject } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { LoyaltyReward } from 'state/loyalty/types';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useGetMenuObjectLazyQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-lazy-query';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { enrichLegacyCartEntryFromMenu } from 'state/unified-menu/utils';
import {
  IAvailabilityObject,
  getAvailabilityStatus,
  isCartEntryAvailableOneMenu,
} from 'utils/availability';
import { getOneMenuAvailability } from 'utils/availability/one-menu-availability';
import { MAX_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';
import { isPicker, isSection, maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';
import { mapBackendToFrontend } from 'utils/reorder';
import {
  convertModActionsToSelections,
  getSelectionsFromMenuData,
  transformMenuObjectToCartItem,
} from 'utils/wizard';

import { useMinimalCart } from '../../../features/cart/hooks/use-minimal-cart';

export const useMapToFrontendCartEntry = () => {
  const { activeDayParts } = useMainMenuContext();
  const getMenuObjectLazyQuery = useGetMenuObjectLazyQuery();
  const { prices, store } = useStoreContext();
  const { vendor } = usePosVendor();
  const { posServiceMode } = useMenuQueryOptions();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu } = useUnifiedMenuContext();
  const { cartEntries } = useCart();
  const { cartEntries: minimalCartEntries } = useMinimalCart();

  const { allRewards } = useLoyaltyRewardsList();

  const allRewardsMap = useMemo(() => {
    const dictionary: Record<string, LoyaltyReward> = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);

  const oneMenuAvailability = useCallback(
    async (
      cartEntry: IBackendCartEntries
    ): Promise<{
      cartEntry: ILegacyCartEntry;
      data: MenuObject | null;
      status: ItemAvailabilityStatus;
    }> => {
      const frontendEntry = mapBackendToFrontend(cartEntry);
      const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);
      const minimalCartEntry = minimalCartEntries?.filter(entry => entry.id === frontendEntry._id);

      if (frontendEntry.isExtra) {
        return { cartEntry: frontendEntry, data: null, status: ItemAvailabilityStatus.CART_EXTRA };
      }

      const menuObject = indexedMenu.find(frontendEntry._id);

      if (!menuObject) {
        return { cartEntry: frontendEntry, data: null, status: ItemAvailabilityStatus.OUT_OF_MENU };
      }

      const isMaxCartQuantityMet =
        (menuObject.quantity?.max ?? MAX_CART_QUANTITY) -
          (cartEntries?.length || 0) -
          (minimalCartEntry?.reduce((acc, entry) => acc + entry.quantity, 0) ?? 0) -
          frontendEntry.quantity <
        0;

      if (isMaxCartQuantityMet) {
        return {
          cartEntry: frontendEntry,
          data: null,
          status: ItemAvailabilityStatus.MAX_CART_QUANTITY_MET,
        };
      }

      let availabilityStatus = getOneMenuAvailability(
        menuObject,
        activeDayParts,
        posServiceMode,
        storeHasBurgersForBreakfast
      );

      if (availabilityStatus === ItemAvailabilityStatus.AVAILABLE) {
        if (!isCartEntryAvailableOneMenu(frontendEntry, indexedMenu, posServiceMode)) {
          availabilityStatus = ItemAvailabilityStatus.UNAVAILABLE;
        }
      }

      const mainEntry = {
        ...enrichLegacyCartEntryFromMenu(frontendEntry, indexedMenu, allRewardsMap),
        reorder: true,
      };

      return { cartEntry: mainEntry, data: null, status: availabilityStatus };
    },
    [
      activeDayParts,
      allRewardsMap,
      cartEntries?.length,
      indexedMenu,
      minimalCartEntries,
      posServiceMode,
      store.hasBurgersForBreakfast,
    ]
  );

  const legacyAvailability = useCallback(
    async (
      cartEntry: IBackendCartEntries,
      cartEntries?: ILegacyCartEntry[]
    ): Promise<{
      cartEntry: ILegacyCartEntry;
      data: MenuObject | null;
      status: ItemAvailabilityStatus;
    }> => {
      const frontendEntry = mapBackendToFrontend(cartEntry);
      const itemId = maybeMapCartEntryToMenuObjectIdentifier(frontendEntry);

      if (!itemId) {
        return { cartEntry: frontendEntry, data: null, status: ItemAvailabilityStatus.UNAVAILABLE };
      }

      const menuObject = await getMenuObjectLazyQuery(itemId);
      // TODO: Replace this cast with auto generated types
      const data = menuObject as MenuObject;

      // Check if Sanity data exist
      if (!data) {
        return { cartEntry: frontendEntry, data: null, status: ItemAvailabilityStatus.OUT_OF_MENU };
      }

      if (cartEntries?.length) {
        const { maxCartQuantityMet } = getMenuObjectCartQuantity({
          menuObject: data,
          quantityToAdd: frontendEntry.quantity,
          cartEntries,
        });
        if (maxCartQuantityMet) {
          return {
            cartEntry: frontendEntry,
            data,
            status: ItemAvailabilityStatus.MAX_CART_QUANTITY_MET,
          };
        }
      }

      // Pickers & Sections are not supported
      if (isSection(data) || isPicker(data)) {
        return { cartEntry: frontendEntry, data: null, status: ItemAvailabilityStatus.UNAVAILABLE };
      }

      const availabilityStatus = getAvailabilityStatus({
        data: data as IAvailabilityObject,
        prices,
        vendor,
        activeDayParts,
        isExtra: frontendEntry.isExtra ?? false,
        storeHasBurgersForBreakfast: Boolean(store.hasBurgersForBreakfast),
      });

      if (availabilityStatus !== ItemAvailabilityStatus.AVAILABLE) {
        return { cartEntry: frontendEntry, data, status: availabilityStatus };
      }

      // get the backend cart entry item from the menu data to ensure up to date selection
      const {
        modifierSelections: modSelectionActions,
        comboSlotSelections,
      } = getSelectionsFromMenuData({
        data,
        selectionsEntry: frontendEntry,
      });

      const modifierSelections = modSelectionActions
        ? convertModActionsToSelections(modSelectionActions)
        : [];

      // create frontend cart entry from menu item, again by not going directly from
      // backend -> frontend, we avoid the reorder possibly adding out of date information to cart
      const { mainEntry } = transformMenuObjectToCartItem(data, {
        quantity: cartEntry.quantity,
        modifierSelections,
        comboSlotSelections,
        pathname: frontendEntry.url,
        reorder: true,
        menuObjectSettings: data.menuObjectSettings,
      });

      if (!mainEntry) {
        return { cartEntry: frontendEntry, data, status: ItemAvailabilityStatus.UNAVAILABLE };
      }

      return { cartEntry: mainEntry, data, status: availabilityStatus };
    },
    [activeDayParts, getMenuObjectLazyQuery, prices, store.hasBurgersForBreakfast, vendor]
  );

  return isUnifiedMenuEnabled ? oneMenuAvailability : legacyAvailability;
};
